import styled from "@emotion/styled";
import { css, keyframes } from "@emotion/react";
import { SelectChangeEvent } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AccidentMarker,
  DangerousDrivingMarker,
  GrayArrowRight,
  IdleTimeMarker,
  LockOpenMarker,
  WarningBatteryMarker,
  WarningEscapeMarker,
} from "../../assets/icons/icons";
import SelectBox from "../../components/selectBox";
import theme from "../../theme";
import { DetectTodayResult, GetMobilityLive } from "../../types/types";
import {
  CodeName,
  DetectItem,
  ImageWrapper,
  InfoWrapper,
  MobilityInfo,
  OtherInfo,
} from "../home/home";
import Spinner from "../../components/spinner";
import { usePutMonitoringCheck } from "../../hooks/usePutMonitoringCheck";
import { DotLoader } from "../../components/dotLoader";
import { getEventOptions } from "../../utils/constants";
import { NotificationBadge } from "../../components/common/NotificationBadge";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import {
  formatDate,
  handleKeyDown,
  textSubstring,
} from "../../utils/util";
import { useWindowSize } from "@reactuses/core";
import { useI18n } from "../../locales";
import { getEventTypeKey } from "../../utils/langUtils";
//import { MobilityListModal } from "./MobilityListModal";

const { md, lg } = theme.screen;

const UtilBarRight: React.FC<{
  data?: GetMobilityLive[];
  setCenter: any;
  setLevel: any;
  select: string;
  setSelect: any;
  selectedInfo?: GetMobilityLive | DetectTodayResult;
  setSelectedInfo: any;
  rightFold: boolean;
  leftFold: boolean;
  setIsButtonVisible: any;
  setBookmarkMarker: any;
  isLoading: boolean;
  activeRight?: boolean;
  setActiveRight?: any;
  setActiveUp?: any;
}> = (props) => {
  const {
    data,
    setCenter,
    setLevel,
    select,
    setSelect,
    selectedInfo,
    setSelectedInfo,
    rightFold,
    leftFold,
    setIsButtonVisible,
    setBookmarkMarker,
    isLoading,
    activeRight,
    setActiveUp,
  } = props;
  const { width } = useWindowSize();
  const { t } = useI18n();
  const [filter, setFilter] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [eventSeqAccident, setEventSeqAccident] = useState(0);
  const [eventSeqBattery, setEventSeqBattery] = useState(0);
  const [eventSeqDanger, setEventSeqDanger] = useState(0);
  const [eventSeqSteal, setEventSeqSteal] = useState(0);
  const [eventSeqUserarea, setEventSeqUserarea] = useState(0);
  const selectedEl = useRef<HTMLDivElement>(null); // 선택된 리스트 포커스 이동
  /*const [openList, setOpenList] = useState(false); // 실시간 운행상황 기기 목록 모달 리스트
  const [runStat, setRunStat] = useState<
    "crash" | "warn" | "run" | "stop"
  >("crash");*/

  const { mutate: patchMonitoringAccident } =
    usePutMonitoringCheck(eventSeqAccident);
  const { mutate: patchMonitoringBattery } =
    usePutMonitoringCheck(eventSeqBattery);
  const { mutate: patchMonitoringDanger } =
    usePutMonitoringCheck(eventSeqDanger);
  const { mutate: patchMonitoringSteal } =
    usePutMonitoringCheck(eventSeqSteal);
  const { mutate: patchMonitoringUserarea } =
    usePutMonitoringCheck(eventSeqUserarea);

  const handleEventCheck = (el: GetMobilityLive) => {
    // 이벤트가 여러개인 경우 각각 seq를 전달해줘야 함 !
    if (el.accident_status_admin === 0 && el.accident_header_seq) {
      setEventSeqAccident(el.accident_header_seq);
    }
    if (el.battery_status_admin === 0 && el.battery_header_seq) {
      setEventSeqBattery(el.battery_header_seq);
    }
    if (el.danger_status_admin === 0 && el.danger_header_seq) {
      setEventSeqDanger(el.danger_header_seq);
    }
    if (el.steal_status_admin === 0 && el.steal_header_seq) {
      setEventSeqSteal(el.steal_header_seq);
    }
    if (el.userarea_status_admin === 0 && el.userarea_header_seq) {
      setEventSeqUserarea(el.userarea_header_seq);
    }
  };

  const handleChange = (event: SelectChangeEvent) => {
    setFilter(Number(event.target.value));
  };

  const handleClose = () => {
    setFilterOpen(false);
  };

  const handleOpen = () => {
    setFilterOpen(true);
  };

  const filteredData: GetMobilityLive[] | undefined = useMemo(() => {
    return data?.filter((el) => {
      if (filter === 0) {
        return el.events;
      } else if (filter === 2) {
        // 차량전복
        return el?.events?.some((event) => {
          const eventKey = getEventTypeKey(event, t);
          return eventKey === "vehicleRolloverEvent" || event === "차량전복";
        });
      } else if (filter === 3) {
        // 위험운전
        return el?.events?.some((event) => {
          const eventKey = getEventTypeKey(event, t);
          return eventKey === "dangerousDrivingEvent" || event === "위험운전";
        });
      } else if (filter === 5) {
        // 시동방치
        return el?.events?.some((event) => {
          const eventKey = getEventTypeKey(event, t);
          return eventKey === "engineAbandonmentEvent" || event === "시동방치";
        });
      } else if (filter === 6) {
        // 사용지역이탈
        return el?.events?.some((event) => {
          const eventKey = getEventTypeKey(event, t);
          return eventKey === "areaExitEvent" || event === "사용지역이탈";
        });
      } else if (filter === 7) {
        // 도난감지
        return el?.events?.some((event) => {
          const eventKey = getEventTypeKey(event, t);
          return eventKey === "theftDetection" || event === "도난감지";
        });
      } else if (filter === 8) {
        // 배터리부족
        return el?.events?.some((event) => {
          const eventKey = getEventTypeKey(event, t);
          return eventKey === "batteryLowEvent" || event.includes("배터리부족");
        });
      } else {
        // 위험지역진입
        return el?.events?.some((event) => {
          const eventKey = getEventTypeKey(event, t);
          return eventKey === "dangerousAreaEntryEvent" || event === "위험지역진입";
        });
      }
    });
  }, [filter, data, t]);

  const handleSetCenterMarker = useCallback(
    (lat: string | number, lng: string | number) => {
      let timeoutId: NodeJS.Timeout | undefined;
      if (width < 451) {
        // 모바일 사이즈
        timeoutId = setTimeout(() => {
          setCenter({
            lat: Number(lat) + 0.0015,
            lng: Number(lng),
          });
        }, 100);
        return () => timeoutId && clearTimeout(timeoutId);
      } else if (width < theme.screen.md) {
        timeoutId = setTimeout(() => {
          setCenter({
            lat: Number(lat) + 0.002,
            lng: Number(lng),
          });
        }, 100);
        return () => timeoutId && clearTimeout(timeoutId);
      } else if (width < theme.screen.lg && !leftFold) {
        timeoutId = setTimeout(() => {
          setCenter({
            lat: Number(lat) + 0.0035,
            lng: Number(lng) - 0.002,
          });
        }, 100);
        return () => timeoutId && clearTimeout(timeoutId);
      } else if (width > theme.screen.lg && !rightFold && leftFold) {
        timeoutId = setTimeout(() => {
          setCenter({
            lat: Number(lat) + 0.0035,
            lng: Number(lng) + 0.002,
          });
        }, 100);
        return () => timeoutId && clearTimeout(timeoutId);
      } else {
        timeoutId = setTimeout(() => {
          setCenter({
            lat: Number(lat) + 0.0035,
            lng: Number(lng),
          });
        }, 100);
        return () => timeoutId && clearTimeout(timeoutId);
      }
    },
    [width]
  );

  useEffect(() => {
    // 이벤트가 여러개인 경우 각각 seq를 전달해줘야 함 !
    if (eventSeqAccident) {
      patchMonitoringAccident();
      setEventSeqAccident(0);
    }
    if (eventSeqBattery) {
      patchMonitoringBattery();
      setEventSeqBattery(0);
    }
    if (eventSeqDanger) {
      patchMonitoringDanger();
      setEventSeqDanger(0);
    }
    if (eventSeqSteal) {
      patchMonitoringSteal();
      setEventSeqSteal(0);
    }
    if (eventSeqUserarea) {
      patchMonitoringUserarea();
      setEventSeqUserarea(0);
    }
  }, [
    eventSeqAccident,
    eventSeqBattery,
    eventSeqDanger,
    eventSeqSteal,
    eventSeqUserarea,
  ]);

  useEffect(() => {
    // 데이터 선택하면 스크롤 이동
    if (rightFold) return; // 접힌 상태
    if (
      window.innerWidth > theme.screen.md &&
      selectedEl &&
      selectedEl.current
    ) {
      selectedEl.current.scrollIntoView();
    }
  }, [selectedInfo, selectedEl, selectedEl.current, rightFold]);

  useEffect(() => {
    // 이벤트가 있는 pid가 한번이라도 선택된 경우 클릭한것으로 간주
    if (!select || !filteredData) return;

    filteredData.map((el) => {
      if (select == el.device_pid) {
        handleEventCheck(el);
      }
    });
  }, [select, filteredData]);

  // console.log(filteredData); // 실시간 감지내역 데이터
  // console.log(data);

  return (
    <Wrapper
      rightFold={rightFold}
      leftFold={leftFold}
      activeRight={activeRight}
    >
      
      <SHeaderWrapper>
        <Header responsive={true}>
          {t.control.realtimeDetectionHistory} -{" "}
          {isLoading ? (
            <div style={{ display: "inline-block", paddingLeft: "10px" }}>
              <DotLoader />
            </div>
          ) : data?.filter((el) => el.events).length ? (
            <span>{`${data?.filter((el) => el.events).length}${t.common.casesUnit}`}</span>
          ) : (
            <span>{`0${t.common.casesUnit}`}</span>
          )}
        </Header>
        <SelectBox
          open={filterOpen}
          handleChange={handleChange}
          handleOpen={handleOpen}
          handleClose={handleClose}
          filter={filter}
          menuItems={getEventOptions(t)}
          title={t.control.selectDetectionType}
          srLabel={t.control.selectDetectionType}
        />
      </SHeaderWrapper>
      <Separator />
      <List>
        {isLoading && <Spinner />}
        {filteredData &&
          filteredData.length !== 0 &&
          filteredData?.map((el) => {
            const isLeased = !!el.lease_name; // 임차인 존재하는지 여부
            return (
              <DetectItem
                ref={
                  el.device_pid === selectedInfo?.device_pid
                    ? selectedEl
                    : null
                }
                active={el.device_pid === selectedInfo?.device_pid && true}
                hoverColor={
                  el.events?.some((event) => {
                    const eventKey = getEventTypeKey(event, t);
                    return eventKey === "vehicleRolloverEvent" || event === "차량전복";
                  })
                    ? theme.color.red_100
                    : el.events === null
                    ? theme.color.neutral_100
                    : theme.color.orange_100
                }
                accentColor={
                  el.events?.some((event) => {
                    const eventKey = getEventTypeKey(event, t);
                    return eventKey === "vehicleRolloverEvent" || event === "차량전복";
                  })
                    ? theme.color.red_500
                    : el.events === null
                    ? theme.color.neutral_500
                    : theme.color.orange_500
                }
                key={el.device_pid.toString()}
                onClick={() => {
                  handleEventCheck(el);
                  setLevel(3);
                  setSelect(el.device_pid);
                  setSelectedInfo(el);
                  setIsButtonVisible(false);
                  setBookmarkMarker({
                    lat: 36.3927,
                    lng: 128.2719,
                    visible: false,
                  });
                  setActiveUp(false);
                  handleSetCenterMarker(el.latitude, el.longitude);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={handleKeyDown}
              >
                <ImageWrapper>
                  {(() => {
                    // 이벤트 타입 확인 (다국어 지원)
                    const isNormal = el.event_accident === "정상" || (t && el.event_accident === t.control.normal);
                    const hasDangerousDriving = el.events?.some((event) => {
                      const eventKey = getEventTypeKey(event, t);
                      return eventKey === "dangerousDrivingEvent" || event === "위험운전";
                    });
                    const hasEngineAbandonment = el.events?.some((event) => {
                      const eventKey = getEventTypeKey(event, t);
                      return eventKey === "engineAbandonmentEvent" || event === "시동방치";
                    });
                    const isUserAreaNormal = el.event_userarea === "정상" || (t && el.event_userarea === t.control.normal);
                    const isBatteryNormal = el.event_battery === "정상" || (t && el.event_battery === t.control.normal);
                    
                    if (!isNormal) {
                      return <AccidentMarker />;
                    } else if (hasDangerousDriving) {
                      return <DangerousDrivingMarker />;
                    } else if (hasEngineAbandonment) {
                      return <IdleTimeMarker />;
                    } else if (!isUserAreaNormal) {
                      return <WarningEscapeMarker />;
                    } else if (!isBatteryNormal) {
                      return <WarningBatteryMarker />;
                    } else {
                      // 도난감지
                      return <LockOpenMarker />;
                    }
                  })()}
                </ImageWrapper>

                <InfoWrapper>
                  <CustomTooltip
                    title={el?.events?.map((event) => {
                      const eventKey = getEventTypeKey(event, t);
                      if (eventKey && eventKey !== "normal") {
                        const translatedName = t.control[eventKey as keyof typeof t.control];
                        // 전압값 등 추가 정보가 있으면 유지
                        const voltageMatch = event.match(/\([^)]*\)/);
                        const bracketMatch = event.match(/\[[^\]]*\]/);
                        const additionalInfo = voltageMatch ? voltageMatch[0] : (bracketMatch ? bracketMatch[0] : "");
                        return additionalInfo ? `${translatedName}${additionalInfo}` : translatedName;
                      }
                      return event;
                    }).join(", ")}
                    active={el.events && el.events.join(", ").length > 20}
                  >
                    <CodeName isRed={el.event_accident !== "정상" || (t && el.event_accident !== t.control.normal)}>
                      {textSubstring(
                        el?.events?.map((event) => {
                          const eventKey = getEventTypeKey(event, t);
                          if (eventKey && eventKey !== "normal") {
                            const translatedName = t.control[eventKey as keyof typeof t.control];
                            // 전압값 등 추가 정보가 있으면 유지 (예: "배터리부족 (0.00V)" -> "Battery Low (0.00V)")
                            const voltageMatch = event.match(/\([^)]*\)/); // 괄호 안의 내용 추출
                            const bracketMatch = event.match(/\[[^\]]*\]/); // 대괄호 안의 내용 추출
                            const additionalInfo = voltageMatch ? voltageMatch[0] : (bracketMatch ? bracketMatch[0] : "");
                            return additionalInfo ? `${translatedName}${additionalInfo}` : translatedName;
                          }
                          return event;
                        }).join(", ") || "",
                        0,
                        20
                      )}

                      {/* 신규 사고 알림 빨간점 */}
                      {(el.accident_status_admin === 0 ||
                        el.battery_status_admin === 0 ||
                        el.danger_status_admin === 0 ||
                        el.steal_status_admin === 0 ||
                        el.userarea_status_admin === 0) && (
                        <NotificationBadge />
                      )}
                    </CodeName>
                  </CustomTooltip>

                  <CustomTooltip
                    title={el?.mobility_name}
                    active={el?.mobility_name?.length > 13}
                  >
                    <MobilityInfo>
                      {t.control.deviceName}: {textSubstring(el.mobility_name, 0, 13)}
                    </MobilityInfo>
                  </CustomTooltip>

                  <CustomTooltip
                    title={el?.name}
                    active={el?.name?.length > 13}
                  >
                    {/* <MobilityInfo>
                      소유자: {textSubstring(el.name, 0, 13)}
                    </MobilityInfo> */}
                    <MobilityInfo>
                      {t.control.owner} :{" "}
                      {textSubstring(
                        isLeased ? el.lease_name : el.name,
                        0,
                        13
                      )}
                    </MobilityInfo>
                  </CustomTooltip>
                  {/* </div> */}
                  <OtherInfo>
                    {t.control.eventDate}:{" "}
                    {(() => {
                      const isNormalValue = (value: string | null | undefined) => {
                        if (!value) return true;
                        return value === "정상" || (t && value === t.control.normal);
                      };
                      
                      const eventDate = [
                        el.event_accident,
                        el.event_battery,
                        el.event_danger,
                        el.event_userarea,
                        el.event_emergency,
                        el.event_keyon,
                      ].find((event) => event && !isNormalValue(event));
                      
                      return eventDate && eventDate.trim() !== ""
                        ? formatDate(eventDate as string, "yyyy-MM-dd HH:mm:ss")
                        : t.common.noData;
                    })()}
                    <span
                      style={{
                        color:
                          el.status === "운행중"
                            ? theme.color.red_500
                            : theme.color.green_500,
                      }}
                    >
                      [{el.status === "운행중" ? t.control.operating : t.control.notOperating}]
                    </span>
                  </OtherInfo>
                </InfoWrapper>
                <GrayArrowRight />
              </DetectItem>
            );
          })}
        {!isLoading && (!filteredData || filteredData.length == 0) && (
          <NoDataMassage>{t.control.noData}</NoDataMassage>
        )}
      </List>

      {/* {openList && (
        <MobilityListModal
          open={openList}
          onPressNo={() => setOpenList(false)}
          runStat={runStat}
        />
      )} */}
    </Wrapper>
  );
};
const utilBarRightClose = keyframes`
from{
  right:0px;
}
to{
  right:-440px;
}
`;
const utilBarRightOpen = keyframes`
from{
  right:-440px;
}
to{
  right:0px;
}
`;

const utilBarLeftClose = keyframes`
from{
  left:0px;
}
to{
  left:-440px;
}
`;

const utilBarLeftOpen = keyframes`
from{
  left:-400px;
}
to{
  left:0px;
}
`;

const Wrapper = styled.div<{
  rightFold: boolean;
  activeRight?: boolean;
  leftFold: boolean;
}>`
  background-color: ${theme.color.neutral_50};
  flex-direction: column;
  display: flex;
  box-shadow: -6px 4px 10px rgba(0, 0, 0, 0.25);
  position: absolute;

  @media (max-width: ${md}px) {
    height: 100%;
    width: 100%;
    box-shadow: none;
  }

  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    position: absolute;

    /* height: 93.6vh; */
    height: calc(100vh - 81px - 76px);
    width: 420px;
    left: 0px;
    top: 80px;
    box-shadow: none;
    overflow-y: auto;
    overflow-x: hidden;

    ${({ leftFold }) =>
      leftFold
        ? css`
            animation: ${utilBarLeftClose} 0.5s ease;
          `
        : !leftFold
        ? css`
            animation: ${utilBarLeftOpen} 0.5s ease;
          `
        : null}
    animation-fill-mode:both;
  }

  @media (min-width: ${lg + 1}px) {
    height: calc(100vh - 130px);
    border-radius: 25px;
    margin-top: 30px;
    margin-right: 20px;
    width: 420px;
    right: 0px;
    z-index: -1;
    overflow-y: auto;
    overflow-x: hidden;
    ${({ rightFold }) =>
      rightFold
        ? css`
            animation: ${utilBarRightClose} 0.5s ease;
          `
        : !rightFold
        ? css`
            animation: ${utilBarRightOpen} 0.5s ease;
          `
        : null}
    animation-fill-mode:both;
  }
`;

const List = styled.div`
  flex: 1;
`;

const Header = styled.strong<{ responsive?: boolean }>`
  display: block;
  padding-top: 25px;
  padding-left: 25px;
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;
  color: ${theme.color.neutral_700};
  margin-bottom: 25px;

  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    display: ${({ responsive }) => !responsive && "none"};
  }
  @media (max-width: ${md}px) {
    padding: 0px;
    margin: 0px;
    font-size: ${theme.fontSize.FONT_1};
    font-weight: ${theme.fontWeight.SEMI_BOLD};
  }
`;

const Separator = styled.div`
  border-bottom: 3px solid ${theme.color.neutral_200};
  @media (min-width: ${md + 1}px) and (max-width: ${lg}px) {
    border-bottom: 1px solid ${theme.color.neutral_200};
  }
`;

const SHeaderWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 19px;
  @media (max-width: ${md}px) {
    align-items: center;
    padding: 10px;
  }
`;

const NoDataMassage = styled.div`
  text-align: center;
  padding: 50px;
  color: ${theme.color.neutral_500};
`;

export default UtilBarRight;
